import { useAuth } from "react-oidc-context";
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { getDisplayUserName } from './utils/authHelpers';
import './App.css';
import './comp/layout.css';

import NoticePage from './comp/NoticePage';
import OrderPage from './comp/OrderPage';
//import OrderStatusPage from './comp/OrderStatusPage';
//import RequestPage from './comp/RequestPage';
import ManagementPage from './comp/ManagementPage';
import AdminPage from './comp/AdminPage';

/**
 * Lambda / API Gateway の戻りをナビ権限フラグ（boolean）に正規化する。
 * 例: { body: "{\"userName\":\"...\",\"normalizedUserName\":\"...\",\"exists\":false}" }
 * → body を JSON パースし、exists が管理画面表示の可否になる。
 */
const coerceNavRoleFlag = (data) => {
  if (data === true || data === false) return data;
  if (typeof data === 'string') return data.trim() === 'true';
  if (data && typeof data === 'object') {
    if ('body' in data) {
      const b = data.body;
      if (typeof b === 'string') {
        try {
          return coerceNavRoleFlag(JSON.parse(b));
        } catch {
          return b.trim() === 'true';
        }
      }
      return coerceNavRoleFlag(b);
    }
    const v =
      data.exists ?? data.result ?? data.allow ?? data.allowed ?? data.isAdmin;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return v.trim() === 'true';
  }
  return false;
};

// ===============================
// ナビゲーションバー（上部メニュー）
// ===============================
// 将来SignOutを実装する可能性がある場合、const Navigation = ({ auth, onSignOut }) => {を使用
// SignOutを使用しないのであれば const auth = useAuth(); で問題なし
const Navigation = ({ showManagementNav }) => {
  // --------------------
  // SSO認証データ取得
  // --------------------
  // SSO認証データ活用宣言 
  const auth = useAuth();
  // 現在のURLパスを取得
  const location = useLocation();
  // 現在のパスと一致する場合にactiveクラスを付与する関数
  const isActive = (path) => {
    return location.pathname === path;
  };
  // SSO認証からSignIn者の名前（表示用ユーザー名（認証時））取得
  // ---共通ヘルパーで custom:displayname の漢字部を取得---
  const userName = getDisplayUserName(auth?.user?.profile ?? {});


  return (
    <nav>
      {/* システム名（タイトル） */}
      <div className="system-title">
        共通消耗品管理システム
      </div>
      {/* 各ページへのリンクとサインイン/アウト */}
      <div className="nav-row">
        <ul>
          <li><Link to="/" className={isActive('/') ? 'active' : ''}>お知らせ</Link></li>
          <li><Link to="/orderpage" className={isActive('/orderpage') ? 'active' : ''}>購入依頼</Link></li>
          {/*<li><Link to="/orderstatuspage" className={isActive('/orderstatuspage') ? 'active' : ''}>購入状況</Link></li>
          <li><Link to="/requestpage" className={isActive('/requestpage') ? 'active' : ''}>共通消耗品登録申請</Link></li>*/}
          {showManagementNav && (
            <li><Link to="/managementpage" className={isActive('/managementpage') ? 'active' : ''}>共通消耗品編集</Link></li>
          )}
          {showManagementNav && (
            <li><Link to="/adminpage" className={isActive('/adminpage') ? 'active' : ''}>管理</Link></li>
          )}
        </ul>
        <div className="nav-auth">
          {auth?.isAuthenticated && userName && (
              <span className="nav-user-name">{userName}</span>
          )}
          {/* 将来機能？【SignOutボタン】
          <button
            type="button"
            className="nav-sign-button"
            onClick={() => auth?.isAuthenticated ? onSignOut() : auth?.signinRedirect()}
          >
            {auth?.isAuthenticated ? "Sign out" : "Sign in"}
          </button>*/}
        </div>
      </div>
    </nav>
  );
};

// ==============================
// アプリ全体のメインコンポーネント
// ==============================
const App = () => {
  // sso認証
  const auth = useAuth();
  const location = useLocation();

  const [id, setId] = useState(null);
  /** ナビの「共通消耗品編集」「管理」表示可否。null は RoleCheck 待ち */
  const [managementNavAllowed, setManagementNavAllowed] = useState(null);
  const [, setNavHeight] = useState(0);

  // 将来機能？【SignOut時の処置】
  // const signOutRedirect = () => {
  //  const clientId = "552kv96pvidbnc93pvqntovvp0";
  //  const logoutUri = process.env.REACT_APP_REDIRECT_URI;
  //  const cognitoDomain = "https://ap-northeast-18w9odbmlj.auth.ap-northeast-1.amazoncognito.com";
  //  window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  //};

  // ナビゲーションバーの高さを保持するstate
  // ナビゲーションバーの高さを取得し、タイトルやmain-contentの位置・高さを動的に調整する関数
  const updateNavHeight = useCallback(() => {
    // nav要素を取得
    const nav = document.querySelector('nav');
    if (nav) {
      // navの高さを取得
      const height = nav.offsetHeight;
      setNavHeight(height);

      // 各ページタイトルの上部余白を調整
      // -20pxはデザイン上の微調整（必要に応じて調整）
      const pageTitles = document.querySelectorAll('.page-title');
      pageTitles.forEach(title => {
        title.style.paddingTop = `${height - 20}px`;
      });
      // main-contentの高さを動的に調整（ナビゲーションバーの高さ分を引く）
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        mainContent.style.height = `calc(100vh - ${height}px)`;
      }
    }
  }, [setNavHeight]);

  // 認証状態の監視とリダイレクト処理
  useEffect(() => {
    if (auth.isLoading) return;
    //if (!auth.isLoading) {
    if (auth.isAuthenticated) {
      setId(auth.user?.profile?.["custom:employeeid"] ?? "");
      return;
    }

    if (auth.error) {
      console.error("認証エラー:", auth.error);
      return;
    }

    // 未認証の場合はログイン画面にリダイレクト
    console.log("未認証のためログイン画面にリダイレクトします");
    auth.signinRedirect();
  }, [auth.isLoading, auth.isAuthenticated, auth.error, auth.user, auth]);

  // 認証後、ナビ表示前に userName を POST し Lambda の true/false で管理系ナビリンクの表示を決定
  useEffect(() => {
    if (auth.isLoading || auth.error || !auth.isAuthenticated) {
      setManagementNavAllowed(null);
      return;
    }

    const userName = getDisplayUserName(auth?.user?.profile ?? {});
    if (!userName) {
      console.warn('RoleCheck: 表示用ユーザー名が取得できませんでした');
      setManagementNavAllowed(false);
      return;
    }

    const url = `https://kd4c0h9pca.execute-api.ap-northeast-1.amazonaws.com/Dev/LambdaToDynamoDB_RoleCheck`;
    const urlWithParams = new URL(url);
    urlWithParams.searchParams.set('page_name', 'Navigation');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(urlWithParams.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userName }),
          signal: controller.signal,
        });
        if (cancelled) return;
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          console.error('RoleCheck HTTP error:', response.status, text);
          setManagementNavAllowed(false);
          return;
        }
        const data = await response.json();
        setManagementNavAllowed(coerceNavRoleFlag(data));
      } catch (e) {
        if (cancelled) return;
        console.error('RoleCheck エラー:', e);
        setManagementNavAllowed(false);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [auth.isLoading, auth.error, auth.isAuthenticated, auth.user]);

  // ページタイトル設定：未認証/ローディング/エラー時は何もしない
  useEffect(() => {
    if (auth.isLoading || auth.error || !auth.isAuthenticated) return;

    // ページタイトル（ブックマーク名）を設定
    switch (location.pathname) {
      case '/':
        document.title = '共通消耗品管理システム';
        break;
      case '/orderpage':
        document.title = '購入依頼 - 共通消耗品管理システム';
        break;
      case '/orderstatuspage':
        document.title = '購入状況 - 共通消耗品管理システム';
        break;
      case '/requestpage':
        document.title = '共通消耗品登録申請 - 共通消耗品管理システム';
        break;
      case '/managementpage':
          document.title = '共通消耗品編集 - 共通消耗品管理システム';
          break;
      case '/adminpage':
          document.title = '管理 - 共通消耗品管理システム';
          break;
      default:
        document.title = '共通消耗品管理システム';
    }
  }, [location.pathname, auth.isLoading, auth.error, auth.isAuthenticated]);

  // 初回レンダリング・リサイズ・ページ遷移時に高さを再計算
  // RoleCheck完了前（managementNavAllowed === null）だと `nav` がDOMに存在しないため、
  // それ以降に再実行する必要がある。
  useEffect(() => {
    if (auth.isLoading || auth.error || !auth.isAuthenticated) return;
    if (managementNavAllowed === null) return;

    updateNavHeight(); // 高さ調整を実行
    window.addEventListener('resize', updateNavHeight); // ウィンドウリサイズ時も再計算
    return () => {
      window.removeEventListener('resize', updateNavHeight);
    };
  }, [
    updateNavHeight,
    location.pathname,
    auth.isLoading,
    auth.error,
    auth.isAuthenticated,
    managementNavAllowed,
  ]);

  // ローディング中
  if (auth.isLoading) {
    return <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>ログイン中...</div>;
  }

  // エラー時
  if (auth.error) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#ff4444' }}>
        認証エラーが発生しました: {auth.error.message}
        <br />
        <button onClick={() => auth.signinRedirect()} style={{ marginTop: '10px', padding: '8px 16px' }}>
          再ログイン
        </button>
      </div>
    );
  }

  // 未認証の場合はローディング表示（リダイレクト処理中）
  if (!auth.isAuthenticated) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>ログイン画面にリダイレクト中...</div>
    );
  }

  // RoleCheck 完了までナビ・メインコンテンツは出さない（userName POST 後にtrue/falseでナビを確定）
  if (managementNavAllowed === null) {
    return (
      <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>権限を確認しています...</div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        {/* ナビゲーションバー（常に上部に固定） */}
        <Navigation showManagementNav={managementNavAllowed} />
        {/* ページ内容部分。ここだけがスクロール対象 */}
        <div className="main-content">
          {/* ルーティングによるページ切り替え */}
          <Routes>
            <Route path="/" element={<NoticePage />} />
            <Route path="/orderpage" element={<OrderPage />} />
            {/*<Route path="/orderstatuspage" element={<OrderStatusPage />} />
            <Route path="/requestpage" element={<RequestPage />} />*/}
            <Route
              path="/managementpage"
              element={
                managementNavAllowed ? <ManagementPage /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="/adminpage"
              element={
                managementNavAllowed ? <AdminPage /> : <Navigate to="/" replace />
              }
            />
          </Routes>
        </div>
      </header>
    </div>
  );
};

export default App;
